<script setup>
/* global axios */
// Box "Aprovações do grupo" — só p/ tickets de GRUPO (WhatsApp via AstraCall).
// Mostra a fila de quem pediu p/ entrar (aprovar/recusar) + toggle do modo
// "só entra com aprovação". Backend faz proxy pro AstraCall
// (kanban/connections/group_requests | group_requests/approve|reject | group_approval).
import { ref, onMounted, onBeforeUnmount, watch, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});
const emit = defineEmits(['count']);
const { t, locale } = useI18n();
const dtLocale = computed(() => locale.value.replace('_', '-'));
const route = useRoute();
const base = `/api/v1/accounts/${route.params.accountId}/kanban/connections`;

const list = ref([]);
const loading = ref(true);
const busy = ref('');
const approvalOn = ref(false);
const savingApproval = ref(false);
// auto-atualiza a fila (a API é sob demanda; sem isso um pedido novo não aparece)
let pollId = null;

const fmtPhone = jid => {
  const digits = String(jid || '')
    .replace(/@.*/, '')
    .replace(/\D/g, '');
  return digits ? `+${digits}` : jid;
};
const fmtWhen = ts =>
  ts ? new Date(ts * 1000).toLocaleString(dtLocale.value) : '';

const load = async () => {
  loading.value = true;
  try {
    list.value =
      (
        await axios.get(`${base}/group_requests`, {
          params: { conversation_id: props.conversationId },
        })
      ).data.data || [];
  } catch (e) {
    list.value = [];
  }
  loading.value = false;
  emit('count', list.value.length); // avisa o pai p/ o badge no título do box
};

const act = async (jid, action) => {
  busy.value = jid;
  try {
    await axios.post(`${base}/group_requests/${action}`, {
      conversation_id: props.conversationId,
      participants: [jid],
    });
    list.value = list.value.filter(r => r.jid !== jid);
    emit('count', list.value.length);
  } catch (e) {
    /* silencioso — o refresh reflete o estado real */
  } finally {
    busy.value = '';
  }
};

const toggleApproval = async val => {
  const prev = approvalOn.value;
  approvalOn.value = val;
  savingApproval.value = true;
  try {
    await axios.put(`${base}/group_approval`, {
      conversation_id: props.conversationId,
      enabled: val,
    });
  } catch (e) {
    approvalOn.value = prev;
  } finally {
    savingApproval.value = false;
  }
};

watch(() => props.conversationId, load);
onMounted(() => {
  load();
  pollId = setInterval(load, 20000);
});
onBeforeUnmount(() => {
  if (pollId) clearInterval(pollId);
});
</script>

<template>
  <div class="flex flex-col gap-2.5 px-3.5 pt-2.5 pb-3.5">
    <!-- toggle: exigir aprovação p/ entrar -->
    <div class="flex flex-col gap-0.5">
      <label
        class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
      >
        <input
          type="checkbox"
          :checked="approvalOn"
          :disabled="savingApproval"
          class="rounded border-n-weak text-n-brand focus:ring-n-brand"
          @change="toggleApproval($event.target.checked)"
        />
        {{ t('KANBAN.groupApproval.requireApproval') }}
      </label>
      <p class="text-xs text-n-slate-10 pl-6">
        {{ t('KANBAN.groupApproval.requireApprovalHint') }}
      </p>
    </div>

    <div
      class="flex items-center justify-between pt-1 border-t border-dashed border-n-weak"
    >
      <span class="text-xs font-medium text-n-slate-11">
        {{ t('KANBAN.groupApproval.queueTitle') }}
        <span
          v-if="list.length"
          class="ml-1 px-1.5 py-0.5 rounded-full bg-n-ruby-9 text-white text-[0.625rem] font-semibold tabular-nums"
          >{{ list.length }}</span
        >
      </span>
      <button
        class="text-xs text-n-blue-11 bg-transparent hover:underline disabled:opacity-50"
        :disabled="loading"
        @click="load"
      >
        {{ t('KANBAN.groupApproval.refresh') }}
      </button>
    </div>

    <div v-if="loading" class="text-xs text-n-slate-10">
      {{ t('KANBAN.groupApproval.loading') }}
    </div>
    <div v-else-if="!list.length" class="text-xs text-n-slate-10">
      {{ t('KANBAN.groupApproval.empty') }}
    </div>
    <template v-else>
      <div
        v-for="r in list"
        :key="r.jid"
        class="flex items-center gap-2 py-1.5 border-b border-n-weak last-of-type:border-b-0"
      >
        <div class="min-w-0 flex-1">
          <div class="text-sm text-n-slate-12 truncate">
            {{ fmtPhone(r.jid) }}
          </div>
          <div class="text-[0.6875rem] text-n-slate-10">
            {{ fmtWhen(r.requestedAt) }}
          </div>
        </div>
        <button
          class="px-2.5 py-1 text-xs font-medium rounded-md bg-n-teal-9 text-white hover:brightness-95 disabled:opacity-50"
          :disabled="busy === r.jid"
          @click="act(r.jid, 'approve')"
        >
          {{ t('KANBAN.groupApproval.approve') }}
        </button>
        <button
          class="px-2.5 py-1 text-xs rounded-md border border-n-weak text-n-slate-11 bg-transparent hover:bg-n-alpha-1 disabled:opacity-50"
          :disabled="busy === r.jid"
          @click="act(r.jid, 'reject')"
        >
          {{ t('KANBAN.groupApproval.reject') }}
        </button>
      </div>
    </template>
  </div>
</template>
